import React, { useState } from 'react';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { Text } from '@components/Text';
import { useAppDispatch } from 'src/hooks';
import {
  replyToContactMessage,
  fetchHostPendingRequests,
} from 'src/redesign/features/Attendees/store/actions';
import { closeCurrentModalAction } from 'src/redesign/features/Modals/store/reducer';
import { showNotification } from 'src/redesign/features/Toast/store/actions';
import moment from 'moment';
import S from './ContactMessageModal.module.scss';

interface ContactMessageModalProps {
  message: any;
}

export const ContactMessageModal: React.FC<ContactMessageModalProps> = ({ message }) => {
  const dispatch = useAppDispatch();
  const [reply, setReply] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!reply.trim()) return;

    setIsSubmitting(true);
    try {
      await dispatch(
        replyToContactMessage({
          messageId: message.id.toString(),
          reply: reply.trim(),
        }),
      ).unwrap();

      // Refresh pending requests
      await dispatch(fetchHostPendingRequests());

      dispatch(
        showNotification({
          severity: 'success',
          detail: 'Reply sent successfully.',
          life: 5000,
        }),
      );

      setReply('');
      dispatch(closeCurrentModalAction());
    } catch (error) {
      console.error('Failed to send reply:', error);
      dispatch(
        showNotification({
          severity: 'error',
          detail: 'Failed to send reply. Please try again.',
          life: 5000,
        }),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    dispatch(closeCurrentModalAction());
  };

  return (
    <div className={S.modalContent}>
      <div className={S.content}>
        <div className={S.messageDetails}>
          <div className={S.field}>
            <Text variation="body-small" bold>
              From:
            </Text>
            <Text variation="body-small">
              {message.sender.name} ({message.sender.email})
            </Text>
          </div>

          <div className={S.field}>
            <Text variation="body-small" bold>
              Subject:
            </Text>
            <Text variation="body-small">{message.subject}</Text>
          </div>

          <div className={S.field}>
            <Text variation="body-small" bold>
              Received:
            </Text>
            <Text variation="body-small">
              {moment(message.created_at).format('MMM D, YYYY h:mm A')}
            </Text>
          </div>

          <div className={S.field}>
            <Text variation="body-small" bold>
              Message:
            </Text>
            <Text variation="body-small" className={S.messageText}>
              {message.message}
            </Text>
          </div>

          {message.status === 'replied' && message.host_reply && (
            <div className={S.replySection}>
              <div className={S.field}>
                <Text variation="body-small" bold>
                  Your Reply:
                </Text>
                <Text variation="body-small" className={S.messageText}>
                  {message.host_reply}
                </Text>
              </div>
              <div className={S.field}>
                <Text variation="caption" color="black-400">
                  Replied on {moment(message.host_replied_at).format('MMM D, YYYY h:mm A')}
                </Text>
              </div>
            </div>
          )}
        </div>

        {message.status === 'pending' && (
          <div className={S.replyInput}>
            <Text variation="body-small" bold className={S.replyLabel}>
              Your Reply:
            </Text>
            <InputTextarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              rows={5}
              placeholder="Type your reply here..."
              className={S.textarea}
              disabled={isSubmitting}
            />
          </div>
        )}
      </div>

      <div className={S.footer}>
        <Button
          label="Cancel"
          onClick={handleCancel}
          className="p-button-text"
          disabled={isSubmitting}
          style={{ color: 'white' }}
        />
        <Button
          label={isSubmitting ? 'Sending...' : 'Send Reply'}
          onClick={handleSubmit}
          disabled={!reply.trim() || isSubmitting || message.status === 'replied'}
        />
      </div>
    </div>
  );
};
